import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaRegStar, FaTrash } from 'react-icons/fa';
import { parseVideoMomentHref, formatVideoMomentLabel, getCachedVideoTitle, fetchVideoTitle } from '../videoMoment';
import {
  CLIP_DELETED_EVENT,
  parseClipHref,
  getCachedClipTitle,
  fetchClipTitle,
  isClipDeleted
} from '../clipShare';

const BODY_MAX = 500;

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

export function chatDateLabel(iso) {
  if (!iso) return '';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '';
  const today = startOfDay(new Date());
  const day = startOfDay(t);
  const diff = Math.round((today - day) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return t.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function chatTimeLabel(iso) {
  if (!iso) return '';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '';
  const s = t.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  // Figure-space pad single-digit hours so AM/PM lines up without a wide column.
  return s.replace(/^(\d):/, '\u2007$1:');
}

function truncate(text, n) {
  const s = String(text || '');
  if (s.length <= n) return s;
  return `${s.slice(0, n - 1)}…`;
}

const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+|\b(?:www\.)?bigplays\.gg(?:\/[^\s]*)?)/gi;

function splitUrl(raw) {
  const trailing = (raw.match(/[),.;!?]+$/) || [''])[0];
  return {
    core: trailing ? raw.slice(0, -trailing.length) : raw,
    trailing
  };
}

function hrefFor(core) {
  if (/^https?:\/\//i.test(core)) return core;
  if (/^www\./i.test(core)) return `https://${core}`;
  return `https://${core}`;
}

function isSameSite(href) {
  try {
    const host = new URL(href).hostname.replace(/^www\./i, '').toLowerCase();
    const here = (typeof window !== 'undefined' ? window.location.hostname : '')
      .replace(/^www\./i, '')
      .toLowerCase();
    return host === 'bigplays.gg' || (here && host === here);
  } catch {
    return false;
  }
}

function sameSitePath(href) {
  try {
    const u = new URL(href);
    return `${u.pathname || '/'}${u.search}${u.hash}`;
  } catch {
    return '/';
  }
}

function ChatPlayLink({ to, className, title, children, onActivate }) {
  const originRef = useRef(null);
  const suppressClickRef = useRef(false);

  return (
    <Link
      to={to}
      className={`${className || ''} touch-manipulation cursor-pointer`}
      title={title}
      onPointerDown={(e) => {
        e.stopPropagation();
        originRef.current = { x: e.clientX, y: e.clientY, type: e.pointerType };
      }}
      onPointerUp={(e) => {
        e.stopPropagation();
        const origin = originRef.current;
        originRef.current = null;
        if (!onActivate) return;
        if (!origin || (origin.type !== 'touch' && origin.type !== 'pen')) return;
        const dx = e.clientX - origin.x;
        const dy = e.clientY - origin.y;
        if ((dx * dx) + (dy * dy) > 64) return;
        suppressClickRef.current = true;
        e.preventDefault();
        onActivate(e);
        window.setTimeout(() => { suppressClickRef.current = false; }, 500);
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!onActivate) return;
        e.preventDefault();
        if (suppressClickRef.current) return;
        onActivate(e);
      }}
    >
      {children}
    </Link>
  );
}

function VideoMomentLink({ path, raw, moment, onVideoMomentClick }) {
  const [title, setTitle] = useState(() => getCachedVideoTitle(moment.videoId));

  useEffect(() => {
    const cached = getCachedVideoTitle(moment.videoId);
    if (cached) {
      setTitle(cached);
      return undefined;
    }
    let live = true;
    fetchVideoTitle(moment.videoId).then((name) => {
      if (live) setTitle(name);
    });
    return () => { live = false; };
  }, [moment.videoId]);

  const displayTitle = truncate(title, 48);
  const label = formatVideoMomentLabel(displayTitle, moment.t);
  return (
    <ChatPlayLink
      to={path}
      className="text-blue-400 underline break-words"
      title={title ? formatVideoMomentLabel(title, moment.t) : raw}
      onActivate={onVideoMomentClick ? (e) => onVideoMomentClick(e, moment) : null}
    >
      {label}
    </ChatPlayLink>
  );
}

function ClipTitleLink({ path, raw, clip, onClipClick }) {
  const [title, setTitle] = useState(() => getCachedClipTitle(clip.clipId));
  const [deleted, setDeleted] = useState(() => isClipDeleted(clip.clipId));

  useEffect(() => {
    if (isClipDeleted(clip.clipId)) {
      setDeleted(true);
      return undefined;
    }
    const cached = getCachedClipTitle(clip.clipId);
    if (cached) setTitle(cached);
    let live = true;
    fetchClipTitle(clip.clipId, { revalidate: true }).then((name) => {
      if (!live) return;
      if (isClipDeleted(clip.clipId)) {
        setDeleted(true);
        return;
      }
      setTitle(name);
    });
    return () => { live = false; };
  }, [clip.clipId]);

  useEffect(() => {
    const onDeleted = (e) => {
      if (e.detail?.clipId == null) return;
      if (String(e.detail.clipId) === String(clip.clipId)) setDeleted(true);
    };
    window.addEventListener(CLIP_DELETED_EVENT, onDeleted);
    return () => window.removeEventListener(CLIP_DELETED_EVENT, onDeleted);
  }, [clip.clipId]);

  if (deleted) {
    return (
      <span className="italic text-gray-400" title={raw}>
        Clip has been deleted
      </span>
    );
  }

  const label = truncate(title, 48) || 'Clip';
  return (
    <ChatPlayLink
      to={path}
      className="inline-flex items-center gap-1.5 align-middle max-w-full no-underline px-2 py-1 min-h-[32px] rounded-full bg-black text-white text-sm font-medium [@media(hover:hover)]:hover:bg-gray-900"
      title={title || raw}
      onActivate={onClipClick ? (e) => onClipClick(e, clip) : null}
    >
      <svg
        className="w-2.5 h-2.5 flex-shrink-0"
        viewBox="0 0 12 12"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M3 1.2v9.6L11 6 3 1.2z" />
      </svg>
      <span className="truncate">{label}</span>
    </ChatPlayLink>
  );
}

export function ChatMessageBody({ text, onVideoMomentClick, onClipClick, className }) {
  const src = String(text || '');
  if (!src) return null;
  const parts = [];
  URL_RE.lastIndex = 0;
  let last = 0;
  let match;
  while ((match = URL_RE.exec(src))) {
    if (match.index > last) {
      parts.push({ type: 'text', value: src.slice(last, match.index) });
    }
    const { core, trailing } = splitUrl(match[0]);
    parts.push({ type: 'url', value: core });
    if (trailing) parts.push({ type: 'text', value: trailing });
    last = match.index + match[0].length;
  }
  if (last < src.length) parts.push({ type: 'text', value: src.slice(last) });

  return (
    <span className={className || 'text-gray-100 whitespace-pre-wrap break-words'}>
      {parts.map((part, i) => {
        if (part.type !== 'url') return <span key={i}>{part.value}</span>;
        const href = hrefFor(part.value);
        const clip = parseClipHref(href);
        if (clip && isSameSite(href)) {
          return (
            <ClipTitleLink
              key={i}
              path={sameSitePath(href)}
              raw={part.value}
              clip={clip}
              onClipClick={onClipClick}
            />
          );
        }
        const moment = parseVideoMomentHref(href);
        if (moment && isSameSite(href)) {
          return (
            <VideoMomentLink
              key={i}
              path={sameSitePath(href)}
              raw={part.value}
              moment={moment}
              onVideoMomentClick={onVideoMomentClick}
            />
          );
        }
        const className = 'text-blue-400 underline break-all';
        if (isSameSite(href)) {
          return (
            <Link
              key={i}
              to={sameSitePath(href)}
              className={className}
              title={part.value}
              onClick={(e) => e.stopPropagation()}
            >
              {part.value}
            </Link>
          );
        }
        return (
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
            onClick={(e) => e.stopPropagation()}
          >
            {part.value}
          </a>
        );
      })}
    </span>
  );
}

const HallChatPanel = ({
  messages,
  hasMore,
  loadingOlder,
  onLoadOlder,
  onSend,
  onKeep,
  onDelete,
  onUsernameClick,
  onVideoMomentClick,
  onClipClick,
  canSpeak,
  canStaff,
  mutedReason,
  policyError,
  onComposerFocusChange
}) => {
  const listRef = useRef(null);
  const rowRefs = useRef({});
  const pinBottomRef = useRef(true);
  const loadingOlderRef = useRef(false);
  const pendingRestoreRef = useRef(null);
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [highlightId, setHighlightId] = useState(null);

  useEffect(() => {
    loadingOlderRef.current = !!loadingOlder;
  }, [loadingOlder]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (pendingRestoreRef.current != null) {
      el.scrollTop = el.scrollHeight - pendingRestoreRef.current;
      pendingRestoreRef.current = null;
      return;
    }
    if (pinBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const grouped = useMemo(() => {
    const out = [];
    let lastLabel = null;
    (messages || []).forEach((msg) => {
      const label = chatDateLabel(msg.createdAt);
      if (label !== lastLabel) {
        out.push({ type: 'date', id: `date-${label}-${msg.id}`, label });
        lastLabel = label;
      }
      out.push({ type: 'msg', msg });
    });
    return out;
  }, [messages]);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinBottomRef.current = distanceFromBottom < 40;
    if (el.scrollTop < 48 && hasMore && !loadingOlderRef.current && onLoadOlder) {
      loadingOlderRef.current = true;
      pendingRestoreRef.current = el.scrollHeight;
      onLoadOlder();
    }
  };

  const send = () => {
    const body = draft.trim();
    if (!body || !canSpeak || !onSend) return;
    onSend(body, replyTo?.id || null);
    setDraft('');
    setReplyTo(null);
    pinBottomRef.current = true;
  };

  const jumpToParent = (msg) => {
    if (!msg?.replyToId) return;
    const node = rowRefs.current[msg.replyToId];
    if (node && listRef.current) {
      node.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setHighlightId(msg.replyToId);
      setTimeout(() => setHighlightId((id) => (id === msg.replyToId ? null : id)), 1600);
    }
  };

  return (
    <div
      className="h-full min-h-0 flex flex-col bg-black/80 border border-gray-700/80 text-white text-xs"
      onTouchStart={(e) => e.stopPropagation()}
      style={{ overscrollBehavior: 'contain', touchAction: 'manipulation' }}
    >
      <div
        ref={listRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto px-1 py-1 space-y-1 bp-hide-scrollbar touch-pan-y"
        style={{ overscrollBehavior: 'contain' }}
      >
        {hasMore && (
          <div className="text-center text-gray-500 py-1">
            {loadingOlder ? 'Loading…' : 'Scroll for older messages'}
          </div>
        )}
        {grouped.map((item) => {
          if (item.type === 'date') {
            return (
              <div key={item.id} className="text-center text-gray-500 py-1 sticky top-0 bg-black/80">
                {item.label}
              </div>
            );
          }
          const msg = item.msg;
          const kept = !!msg.savedAt;
          return (
            <div
              key={msg.id}
              ref={(el) => { rowRefs.current[msg.id] = el; }}
              className={`group rounded px-0.5 py-0.5 ${
                highlightId === msg.id
                  ? 'bg-blue-900/50'
                  : kept
                    ? 'bg-white/10'
                    : '[@media(hover:hover)]:hover:bg-white/5'
              }`}
            >
              {msg.replyToId && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => jumpToParent(msg)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      jumpToParent(msg);
                    }
                  }}
                  className="block w-full text-left text-gray-400 border-l-2 border-gray-600 pl-1.5 mb-0.5 truncate cursor-pointer"
                >
                  @{msg.replyToUsername || 'unknown'}{' '}
                  <ChatMessageBody
                    text={msg.replyToBody}
                    onVideoMomentClick={onVideoMomentClick}
                    onClipClick={onClipClick}
                    className="text-gray-400"
                  />
                </div>
              )}
              <div className="flex items-start gap-1">
                <span className="text-gray-500 tabular-nums whitespace-nowrap flex-shrink-0 pt-0.5">
                  {chatTimeLabel(msg.createdAt)}
                </span>
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => onUsernameClick && onUsernameClick(msg)}
                    className="font-semibold text-blue-300 hover:underline mr-1"
                  >
                    {msg.username}:
                  </button>
                  <ChatMessageBody
                    text={msg.body}
                    onVideoMomentClick={onVideoMomentClick}
                    onClipClick={onClipClick}
                  />
                </div>
                <div className="flex-shrink-0 flex items-start gap-1">
                  {canSpeak && (
                    <button
                      type="button"
                      title="Reply"
                      onClick={() => setReplyTo(msg)}
                      className="text-gray-500 hover:text-blue-300 opacity-0 group-hover:opacity-100 focus:opacity-100"
                    >
                      Reply
                    </button>
                  )}
                  {kept && !canStaff && (
                    <span className="text-yellow-400 pt-0.5" title="Kept">
                      <FaStar />
                    </span>
                  )}
                  {canStaff && (
                    <>
                      <button
                        type="button"
                        title={kept ? 'Un-keep' : 'Keep'}
                        onClick={() => onKeep && onKeep(msg, !kept)}
                        className={kept
                          ? 'text-yellow-400'
                          : 'text-gray-500 hover:text-yellow-400 opacity-0 group-hover:opacity-100 focus:opacity-100'}
                      >
                        {kept ? <FaStar /> : <FaRegStar />}
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        onClick={() => onDelete && onDelete(msg)}
                        className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 focus:opacity-100"
                      >
                        <FaTrash />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {replyTo && (
        <div className="flex items-center gap-2 px-2 py-1 border-t border-gray-700 bg-gray-900/80">
          <div className="min-w-0 flex-1 truncate text-gray-400">
            Replying to <span className="text-blue-300">{replyTo.username}</span>
            {' '}
            <ChatMessageBody
              text={replyTo.body}
              onVideoMomentClick={onVideoMomentClick}
              onClipClick={onClipClick}
              className="text-gray-400"
            />
          </div>
          <button
            type="button"
            onClick={() => setReplyTo(null)}
            className="text-gray-500 hover:text-white flex-shrink-0"
          >
            ×
          </button>
        </div>
      )}

      <div className="flex-shrink-0 border-t border-gray-700 p-1.5" style={{ touchAction: 'none' }}>
        {policyError && canSpeak && (
          <div className="text-amber-400 px-1 pb-1">{policyError}</div>
        )}
        {canSpeak ? (
          <div className="flex items-stretch gap-1.5">
            <textarea
              value={draft}
              maxLength={BODY_MAX}
              rows={1}
              inputMode="text"
              enterKeyHint="send"
              autoComplete="off"
              autoCorrect="on"
              autoCapitalize="sentences"
              placeholder={replyTo ? 'Write a reply…' : 'Message this hall…'}
              onChange={(e) => setDraft(e.target.value)}
              onFocus={() => onComposerFocusChange && onComposerFocusChange(true)}
              onBlur={() => onComposerFocusChange && onComposerFocusChange(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              className="flex-1 min-w-0 resize-none bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 leading-none"
              style={{ fontSize: 16, touchAction: 'none' }}
            />
            <button
              type="button"
              onClick={send}
              disabled={!draft.trim()}
              className="flex-shrink-0 px-2.5 rounded bg-blue-700 hover:bg-blue-600 disabled:opacity-40"
            >
              Send
            </button>
          </div>
        ) : (
          <div className="text-amber-400 px-1 py-1">
            {mutedReason || 'You cannot chat in this hall right now.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default HallChatPanel;
